import { Component, ElementRef, Input, AfterViewInit, OnDestroy } from '@angular/core';

import * as _ from 'lodash';
import PopperJS from 'popper.js';
import { ResizeSensor } from 'css-element-queries';

@Component({
  selector: 'uex-popover',
  template: '<ng-content></ng-content>',
  host: {
    class: 'uex-popover',
    '[class.visible]': 'visible',
    '[style.width.px]': 'size.width',
    '[style.min-width.px]': 'size.minWidth',
    '[style.max-height.px]': 'size.maxHeight'
  }
})
export class PopoverComponent implements AfterViewInit, OnDestroy {

  @Input()
    set target(target: any) {
      setTimeout(() => {
        this._target = target;
        this.update();
      });
    }
    get target(): any { return this._target; }
  @Input()
    set visible(visible: boolean) {
      setTimeout(() => {
        this._visible = visible;
        this._updatePopper();
      });
    }
    get visible(): boolean { return this._visible; }
  @Input()
    set placement(placement: PopperJS.Placement) { this._placement = placement; this.update(); }
    get placement(): PopperJS.Placement { return this._placement; }
  @Input()
    set useTargetWidth(use: boolean) {
      setTimeout(() => {
        this._use_target_width = use;
        this.update();
      });
    }
    get useTargetWidth(): boolean { return this._use_target_width; }
  @Input()
    set useTargetMinWidth(use: boolean) {
      setTimeout(() => {
        this._use_target_min_width = use;
        this.update();
      });
    }
    get useTargetMinWidth(): boolean { return this._use_target_min_width; }

  constructor(
    public elementRef: ElementRef
  ) { }

  private _popper: PopperJS;
  private _target: any;
  private _visible = false;
  private _placement: PopperJS.Placement = 'bottom-start';
  private _use_target_width = false;
  private _use_target_min_width = false;
  private _resize_sensor: ResizeSensor;

  private _default_size: IPopoverSize = {
    width: null,
    minWidth: null,
    maxHeight: null
  };
  public size: IPopoverSize = _.clone(this._default_size);

  public ngAfterViewInit() {
    this._resize_sensor = new ResizeSensor(this.elementRef.nativeElement, () => this._updatePopper());
  }

  public ngOnDestroy() {
    if (this._resize_sensor) { this._resize_sensor.detach(); }
    if (this._popper) { this._popper.destroy(); }
  }

  public show() {
    this.visible = true;
  }

  public hide() {
    this.visible = false;
  }

  public update() {
    if (!this.visible) { return; }
    this._setPopper();
  }

  private _setPopper() {
    if (this._popper) {
      this._popper.destroy();
      this._popper = null;
    }

    const target = this._getTarget();

    if (!target) { return; }

    setTimeout(() => {
      this._popper = new PopperJS(target, this.elementRef.nativeElement, {
        placement: this._placement,
        positionFixed: true,
        onUpdate: () => this._setSize()
      });
      this._popper.update();
    });
  }

  private _updatePopper() {
    if (this._popper) {
      this._popper.update();
    } else {
      this._setPopper();
    }
  }

  private _setSize() {
    if (!this.target) { return; }

    this.size = _.clone(this._default_size);

    const target = this._getTarget();
    const target_rect = target.getBoundingClientRect();

    if (this.useTargetWidth) {
      this.size.width = target_rect.width;
    }
    if (this.useTargetMinWidth) {
      this.size.minWidth = target_rect.width;
    }

    this._setMaxHeight();
  }

  private _setMaxHeight() {
    const scroll_parent = this._getScrollParent();
    if (!scroll_parent) { return; }

    const parent_rect = scroll_parent.getBoundingClientRect();
    this.size.maxHeight = parent_rect.height * 0.8;
  }

  private _getTarget(): HTMLElement {
    return this.target.elementRef ? this.target.elementRef.nativeElement : this.target;
  }

  private _getScrollParent(node = this.elementRef.nativeElement) {
    if (node == null) {
      return null;
    }

    if (node != this.elementRef.nativeElement && node.scrollHeight > node.clientHeight) {
      return node;
    } else {
      return this._getScrollParent(node.parentNode);
    }
  }
}

export interface IPopoverSize {
  width: number;
  minWidth: number;
  maxHeight: number;
}
